// ============================================================
// SaveSlotCard — reusable bronze save-slot card for the multiplayer lobby
// ============================================================
//
// Renders: framed bronze panel, corner L-accents, account name at top, level
// label, and a row of hero portraits. Designed for the left-side "my save"
// and right-side "opponent save" cards in the multiplayer lobby.
//
// Pragmatic scope: this is a lobby-focused clone of the MenuScene's inline
// card renderer — without the menu-specific extras (biome backdrops, sigil
// badges, inferno variants, active-slot glow). MenuScene keeps its bespoke
// renderer; this one serves the lobby and is testable in isolation.

import Phaser from 'phaser';

export interface SaveSlotInfoLike {
  exists: boolean;
  level: number;
  heroLevel: number;
  heroes: number;
  faction: string;
  heroName: string;
  heroClass: string;
  heroKey?: string;
  setupComplete: boolean;
  heroData: { heroKey?: string; heroClass: string; name: string; level: number }[];
  infernoOnly?: boolean;
}

export interface SaveSlotCardOpts {
  /** Center x/y of the card. */
  x: number;
  y: number;
  /** Card dimensions. Typical: 152×132 (menu size) or 228×198 (lobby 1.5×). */
  width: number;
  height: number;
  /** The save data to render. null → "no save" placeholder. */
  info: SaveSlotInfoLike | null;
  /** Account display name (from user.username). Shown at top of card. */
  accountName: string;
  /** Mode theme accent color for stroke (0x8b2000 PVP, 0x5a7030 Coop, 0xc8973a default). */
  accentColor?: number;
}

export interface SaveSlotCardHandle {
  /** Swap stroke to the selected-highlight color (gold). */
  setSelected(selected: boolean): void;
  /** Tear down every Phaser object the card created. */
  destroy(): void;
}

// Color tokens — bronze palette matching MenuScene.
const PANEL_BG = 0x0d0a0e;
const PANEL_BG_ALPHA = 0.95;
const DEFAULT_ACCENT = 0xc8973a;
const SELECTED_STROKE = 0xffd700;
const TITLE_COLOR = '#c8973a';
const META_COLOR = '#8a6a28';
const EMPTY_COLOR = '#5a4830';

export function buildSaveSlotCard(
  scene: Phaser.Scene,
  opts: SaveSlotCardOpts,
): SaveSlotCardHandle {
  const { x, y, width, height, info, accountName } = opts;
  const accent = opts.accentColor ?? DEFAULT_ACCENT;

  // ── Outer bronze panel ──
  const bg = scene.add.rectangle(x, y, width, height, PANEL_BG, PANEL_BG_ALPHA)
    .setStrokeStyle(2, accent, 1)
    .setDepth(1);

  // Inner accent line for the layered look
  const inner = scene.add.rectangle(x, y, width - 6, height - 6, 0, 0)
    .setStrokeStyle(1, accent, 0.3)
    .setDepth(1);

  // Corner L-shaped accents
  const corners = scene.add.graphics().setDepth(2);
  drawCorners(corners, x, y, width, height, accent);

  // ── Labels: account name (top) + level (bottom) ──
  const topY = y - height / 2 + 18;
  const bottomY = y + height / 2 - 16;

  const accountTxt = scene.add.text(x, topY, accountName, {
    fontSize: `${Math.round(width / 16)}px`,
    fontFamily: "'Cinzel Decorative', Georgia, serif",
    color: TITLE_COLOR,
    fontStyle: 'bold',
  }).setOrigin(0.5).setDepth(3);

  let levelTxt: Phaser.GameObjects.Text | null = null;
  let placeholderTxt: Phaser.GameObjects.Text | null = null;
  const portraitObjects: Phaser.GameObjects.GameObject[] = [];

  if (info) {
    levelTxt = scene.add.text(x, bottomY, `Lv ${info.level}`, {
      fontSize: `${Math.round(width / 20)}px`,
      fontFamily: 'Georgia, serif',
      color: META_COLOR,
    }).setOrigin(0.5).setDepth(3);

    // ── Hero portraits row ──
    // Layout: main (center) larger, side portraits flanking, all centered
    // vertically in the card's body area (below title, above level label).
    const bodyCenterY = y - 6;
    const heroes = info.heroData.slice(0, 3);
    const mainSize = Math.round(width * 0.38);
    const sideSize = Math.round(width * 0.22);

    const positions: Array<{ x: number; y: number; size: number; flip: boolean }> = [];
    if (heroes.length === 1) {
      positions.push({ x, y: bodyCenterY, size: mainSize, flip: false });
    } else if (heroes.length === 2) {
      positions.push({ x: x - mainSize * 0.6, y: bodyCenterY, size: mainSize, flip: false });
      positions.push({ x: x + mainSize * 0.6, y: bodyCenterY, size: sideSize, flip: false });
    } else if (heroes.length >= 3) {
      positions.push({ x: x - mainSize * 0.65, y: bodyCenterY, size: sideSize, flip: true });
      positions.push({ x, y: bodyCenterY, size: mainSize, flip: false });
      positions.push({ x: x + mainSize * 0.65, y: bodyCenterY, size: sideSize, flip: false });
    }

    heroes.forEach((h, i) => {
      const pos = positions[i];
      if (!pos) return;
      const key = [h.heroKey, h.heroClass]
        .filter((k): k is string => !!k)
        .find((k) => scene.textures.exists(`hero_${k}`));
      if (key) {
        const sprite = scene.add.sprite(pos.x, pos.y, `hero_${key}`)
          .setDisplaySize(pos.size, pos.size)
          .setOrigin(0.5)
          .setDepth(2 + i * 0.1)
          .setFlipX(pos.flip);
        // Mirror MenuScene's hero-portrait pipeline (MenuScene.ts:685-691):
        // apply LINEAR filter for cleaner upscaling, then play the registered
        // idle anim so lobby cards animate just like the main menu cards.
        const tex = scene.textures.get(`hero_${key}`);
        const src = tex && tex.source && tex.source[0];
        if (src && typeof (src as { setFilter?: (m: number) => void }).setFilter === 'function') {
          (src as { setFilter: (m: number) => void }).setFilter(Phaser.Textures.FilterMode.LINEAR);
        }
        const idleKey = `hero_${key}_idle`;
        if (scene.anims?.exists?.(idleKey)) sprite.play(idleKey);
        portraitObjects.push(sprite);
      } else {
        // Fallback: circle with class initial when sprite not loaded (dev mode)
        const circle = scene.add.rectangle(pos.x, pos.y, pos.size, pos.size, 0x1a1208, 0.9)
          .setStrokeStyle(1, accent, 0.6)
          .setDepth(2 + i * 0.1);
        const initial = scene.add.text(pos.x, pos.y, h.heroClass.charAt(0).toUpperCase(), {
          fontSize: `${Math.round(pos.size * 0.5)}px`,
          fontFamily: 'Georgia, serif',
          color: META_COLOR,
        }).setOrigin(0.5).setDepth(3 + i * 0.1);
        portraitObjects.push(circle, initial);
      }
      // Small name label under each portrait
      const nameTxt = scene.add.text(pos.x, pos.y + pos.size / 2 + 6, h.name, {
        fontSize: '9px',
        fontFamily: 'Georgia, serif',
        color: META_COLOR,
      }).setOrigin(0.5).setDepth(3 + i * 0.1);
      portraitObjects.push(nameTxt);
    });
  } else {
    placeholderTxt = scene.add.text(x, y, 'No save', {
      fontSize: '12px',
      fontFamily: 'Georgia, serif',
      color: EMPTY_COLOR,
      fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(3);
  }

  return {
    setSelected(selected: boolean): void {
      bg.setStrokeStyle(selected ? 3 : 2, selected ? SELECTED_STROKE : accent, 1);
    },
    destroy(): void {
      bg.destroy();
      inner.destroy();
      corners.destroy();
      accountTxt.destroy();
      levelTxt?.destroy();
      placeholderTxt?.destroy();
      for (const o of portraitObjects) o.destroy();
    },
  };
}

/** Paints four L-shaped corner accents — same bronze motif as the MenuScene. */
function drawCorners(
  gfx: Phaser.GameObjects.Graphics,
  cx: number, cy: number, cw: number, ch: number,
  color: number,
): void {
  gfx.lineStyle(1, color, 0.8);
  const cl = 10;
  const hw = cw / 2 - 4;
  const hh = ch / 2 - 4;
  gfx.strokePoints([{ x: cx - hw, y: cy - hh + cl }, { x: cx - hw, y: cy - hh }, { x: cx - hw + cl, y: cy - hh }], false);
  gfx.strokePoints([{ x: cx + hw - cl, y: cy - hh }, { x: cx + hw, y: cy - hh }, { x: cx + hw, y: cy - hh + cl }], false);
  gfx.strokePoints([{ x: cx - hw, y: cy + hh - cl }, { x: cx - hw, y: cy + hh }, { x: cx - hw + cl, y: cy + hh }], false);
  gfx.strokePoints([{ x: cx + hw - cl, y: cy + hh }, { x: cx + hw, y: cy + hh }, { x: cx + hw, y: cy + hh - cl }], false);
}
